import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, Sparkles, Sun, CheckCircle, Palette, Eye, Car } from "lucide-react";
import heroImage from "@/assets/hero-studio-luxury.jpg";
import xpelLogo from "@/assets/xpel-logo.png";
import heroPPF from "@/assets/hero-ppf.jpg";
import heroCeramic from "@/assets/hero-ceramic.jpg";
import heroWindowTint from "@/assets/hero-window-tint.webp";
import heroVinylWraps from "@/assets/hero-vinyl-wraps.webp";
import heroWindshield from "@/assets/hero-windshield.webp";
import TestimonialSection from "@/components/TestimonialSection";
const Home = () => {
  return <div>
      <Helmet>
        <title>Battle Born Protection Films | PPF & Ceramic Coating Las Vegas</title>
        <meta name="description" content="Las Vegas's XPEL-certified paint protection film (PPF) and ceramic coating studio. Protecting luxury and exotic vehicles from rock chips, UV damage, and scratches. Serving Las Vegas, Henderson & Summerlin. Call (702) 559-9365." />
        <link rel="canonical" href="https://battlebornppf.com/" />
        <link rel="preload" as="image" href={heroImage} />
      </Helmet>
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          {/* Video background support - replace with video element when available */}
          {/* <video 
            autoPlay 
            muted 
            loop 
            playsInline
            className="w-full h-full object-cover"
           >
            <source src="/path-to-video.mp4" type="video/mp4" />
           </video> */}
          <img src={heroImage} alt="Luxury vehicle with premium paint protection" className="w-full h-full object-cover" width="1504" height="846" loading="eager" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        </div>
        
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-heading font-bold mb-6 leading-tight">The Ultimate Protection for Your Vehicle</h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
              Las Vegas's premier XPEL Certified installer. Premium protection for luxury, exotic, and enthusiast vehicles.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild variant="hero" size="lg">
                <Link to="/contact">Request Free Quote</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white/10">
                <Link to="/services">Explore Services</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-card border-y border-border py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            <div className="flex items-center gap-3">
              <img src={xpelLogo} alt="XPEL Certified Installer" className="h-12 invert" width="48" height="48" loading="lazy" />
              <span className="text-sm font-medium">Certified Installer</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-accent" />
              <span className="text-sm">20+ Years Experience</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-accent" />
              <span className="text-sm">Lifetime Warranty</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-accent" />
              <span className="text-sm">Climate-Controlled Facility</span>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 md:py-20 bg-[hsl(0_0%_13%)]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
              Las Vegas's XPEL-Certified PPF & Ceramic Coating Studio
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Battle Born Protection Films is a locally owned automotive protection studio in Las Vegas, Nevada, specializing in paint protection film (PPF), ceramic coatings, window tint, and vinyl wraps. As an XPEL-certified installer, our technicians have completed XPEL's rigorous training program and are authorized to install genuine XPEL products backed by a 10-year manufacturer warranty. Located at 6960 W Russell Rd B in southwest Las Vegas, we serve vehicle owners across the Las Vegas Valley including Henderson, Summerlin, and North Las Vegas. We specialize in luxury and exotic vehicles where precision installation is critical — every job is performed in our climate-controlled, dust-filtered facility to ensure flawless results. Call (702) 559-9365 for a free quote.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <Card className="p-6">
              <Shield className="w-10 h-10 text-accent mb-4" />
              <h3 className="text-xl font-heading font-semibold mb-2">XPEL Certified Installation</h3>
              <p className="text-sm text-muted-foreground">
                We are authorized by XPEL to install their full product line — ULTIMATE PLUS, STEALTH, and Prime window films — with full 10-year manufacturer warranty backing on every PPF application.
              </p>
            </Card>

            <Card className="p-6">
              <Car className="w-10 h-10 text-accent mb-4" />
              <h3 className="text-xl font-heading font-semibold mb-2">Luxury & Exotic Vehicle Specialists</h3>
              <p className="text-sm text-muted-foreground">
                From daily drivers to Ferraris and Lamborghinis, our technicians handle complex panel geometry, factory wrap edges, and paint-sensitive surfaces with precision and care.
              </p>
            </Card>

            <Card className="p-6">
              <Sun className="w-10 h-10 text-accent mb-4" />
              <h3 className="text-xl font-heading font-semibold mb-2">Las Vegas Climate Expertise</h3>
              <p className="text-sm text-muted-foreground">
                We understand the specific challenges Nevada's 110°F heat, extreme UV, and alkaline hard water create for vehicle paint. Every product we recommend is selected for Las Vegas conditions.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-darker-bg">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Premium Protection Solutions
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Comprehensive automotive protection services designed for discerning vehicle owners
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Paint Protection Film */}
            <Card className="group hover:border-accent transition-all duration-300 bg-card overflow-hidden">
              <div className="relative h-48 overflow-hidden">
                <img src={heroPPF} alt="XPEL Paint Protection Film Las Vegas - PPF Installation" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" width="411" height="233" loading="lazy" />
              </div>
              <CardContent className="p-6">
                <Shield className="w-10 h-10 text-accent mb-4" />
                <h3 className="text-2xl font-heading font-bold mb-3">Paint Protection Film</h3>
                <p className="text-muted-foreground mb-6">
                  XPEL Ultimate Plus self-healing film protects against rock chips, scratches, and environmental damage.
                </p>
                <Button asChild variant="premium" className="w-full">
                  <Link to="/services/paint-protection-film">Learn More<span className="sr-only"> about Paint Protection Film</span></Link>
                </Button>
              </CardContent>
            </Card>

            {/* Ceramic Coatings */}
            <Card className="group hover:border-accent transition-all duration-300 bg-card overflow-hidden">
              <div className="relative h-48 overflow-hidden">
                <img src={heroCeramic} alt="XPEL Fusion Plus Ceramic Coating Las Vegas" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" width="411" height="233" loading="lazy" />
              </div>
              <CardContent className="p-6">
                <Sparkles className="w-10 h-10 text-accent mb-4" />
                <h3 className="text-2xl font-heading font-bold mb-3">Ceramic Coatings</h3>
                <p className="text-muted-foreground mb-6">
                  Advanced nano-ceramic coatings deliver unmatched gloss, hydrophobic protection, and durability.
                </p>
                <Button asChild variant="premium" className="w-full">
                  <Link to="/services/ceramic-coatings">Learn More<span className="sr-only"> about Ceramic Coatings</span></Link>
                </Button>
              </CardContent>
            </Card>

            {/* Window Tint */}
            <Card className="group hover:border-accent transition-all duration-300 bg-card overflow-hidden">
              <div className="relative h-48 overflow-hidden">
                <img src={heroWindowTint} alt="XPEL Window Tint Las Vegas - Ceramic Film" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" width="411" height="233" loading="lazy" />
              </div>
              <CardContent className="p-6">
                <Sun className="w-10 h-10 text-accent mb-4" />
                <h3 className="text-2xl font-heading font-bold mb-3">Window Tint</h3>
                <p className="text-muted-foreground mb-6">
                  Premium ceramic window films for vehicles and buildings. Solar control, privacy, and UV protection.
                </p>
                <Button asChild variant="premium" className="w-full">
                  <Link to="/services/window-tint">Learn More<span className="sr-only"> about Window Tint</span></Link>
                </Button>
              </CardContent>
            </Card>

            {/* Vinyl Wraps & Graphics */}
            <Card className="group hover:border-accent transition-all duration-300 bg-card overflow-hidden">
              <div className="relative h-48 overflow-hidden">
                <img src={heroVinylWraps} alt="Vinyl Wraps & Graphics Las Vegas" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" width="411" height="233" loading="lazy" />
              </div>
              <CardContent className="p-6">
                <Palette className="w-10 h-10 text-accent mb-4" />
                <h3 className="text-2xl font-heading font-bold mb-3">Vinyl Wraps & Graphics</h3>
                <p className="text-muted-foreground mb-6">
                  Full color changes and commercial branding. Transform your vehicle or turn your fleet into mobile billboards.
                </p>
                <Button asChild variant="premium" className="w-full">
                  <Link to="/services/vinyl-wraps">Learn More<span className="sr-only"> about Vinyl Wraps and Graphics</span></Link>
                </Button>
              </CardContent>
            </Card>

            {/* Windshield Protection */}
            <Card className="group hover:border-accent transition-all duration-300 bg-card overflow-hidden">
              <div className="relative h-48 overflow-hidden">
                <img src={heroWindshield} alt="XPEL Windshield Protection Film Las Vegas" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" width="411" height="233" loading="lazy" />
              </div>
              <CardContent className="p-6">
                <Eye className="w-10 h-10 text-accent mb-4" />
                <h3 className="text-2xl font-heading font-bold mb-3">Windshield Protection</h3>
                <p className="text-muted-foreground mb-6">
                  XPEL windshield film prevents rock chips and pitting. Optical clarity with superior impact protection.
                </p>
                <Button asChild variant="premium" className="w-full">
                  <Link to="/services/windshield-protection">Learn More<span className="sr-only"> about Windshield Protection</span></Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Why Battle Born?
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Uncompromising quality and craftsmanship for your automotive investment
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-accent/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-xl font-heading font-semibold mb-2">XPEL Certified</h3>
              <p className="text-muted-foreground">
                Factory-trained installers using premium XPEL products
              </p>
            </div>

            <div className="text-center">
              <div className="bg-accent/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-xl font-heading font-semibold mb-2">Precision Installation</h3>
              <p className="text-muted-foreground">
                Computer-cut patterns and expert installation for flawless results
              </p>
            </div>

            <div className="text-center">
              <div className="bg-accent/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-xl font-heading font-semibold mb-2">Lifetime Warranty</h3>
              <p className="text-muted-foreground">
                Comprehensive warranty coverage for your peace of mind
              </p>
            </div>

            <div className="text-center">
              <div className="bg-accent/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sun className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-xl font-heading font-semibold mb-2">Climate Controlled</h3>
              <p className="text-muted-foreground">
                Professional facility ensuring optimal installation conditions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialSection />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent/20 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Ready to Protect Your Investment?
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Get a free quote and consultation for your vehicle's protection needs
          </p>
          <Button asChild variant="hero" size="lg">
            <Link to="/contact">Request Free Quote</Link>
          </Button>
        </div>
      </section>

    </div>;
};
export default Home;